import { background, reference } from '@carvana/ds-styles';
import { useCallback, useContext, useMemo } from 'react';
import { appAssetsCdnUrl } from '~/constants/environment';
import ModalContext from '~/context/home/ModalContext';
import useTradeCtaClick from '~/hooks/common/appraisal/useTradeCtaClick';
import { nativeStcPayload } from '~/native/common/utils';
import optimizeImage, {
  type Options as OptimizeOptions
} from '~/utilities/optimizeImage';
import type { SlideProps } from './Slide';

const commonOptions: OptimizeOptions = {
  format: 'auto',
  quality: 90
};

type SlideContent = Omit<
  SlideProps,
  'opacity' | 'spacerHeading' | 'spacerSubHeading'
>;

export default function useSlideContents(
  onWatchVideoClick: () => void
): SlideContent[] {
  const { setAppraisalEmbeddedModalOpen } = useContext(ModalContext);
  const openCarouselEmbeddedModal = useCallback(
    (isOpen: boolean) => setAppraisalEmbeddedModalOpen(isOpen, 'home-carousel'),
    [setAppraisalEmbeddedModalOpen]
  );
  const {
    isEligible: isTradeCtaEligible,
    onClick: handleAppraisalModalCtaClick
  } = useTradeCtaClick(openCarouselEmbeddedModal);

  return useMemo<SlideContent[]>(
    () => [
      {
        backgroundColor: reference.slate['700'],
        cta: {
          action: onWatchVideoClick,
          text: 'Play'
        },
        heading: 'Experience Shaq’s kind of car-buying',
        image: {
          alt: 'Shaquille O’Neal smiling and pointing at the camera from a desk with a miniature Carvana car vending machine',
          desktop: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/save-time-shaq-desktop.jpg`,
            { ...commonOptions, height: 1000 }
          ),
          mobile: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/save-time-shaq-mobile.jpg`,
            commonOptions
          ),
          tablet: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/save-time-shaq-tablet.jpg`,
            { ...commonOptions, height: 600 }
          )
        },
        showPlayButton: true,
        subHeading:
          'Buy your car 100% online and pick it up at a Carvana Vending Machine. You just can’t stay in it.'
      },
      {
        backgroundColor: reference.slate['700'],
        cta: {
          action: '/value-tracker',
          text: 'Get Your Value'
        },
        heading: 'Stay up to speed with your car’s value',
        image: {
          alt: 'A man on a couch looking at his phone',
          desktop: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/value-tracker-desktop.jpg`,
            commonOptions
          ),
          mobile: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/value-tracker-mobile.jpg`,
            commonOptions
          ),
          tablet: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/value-tracker-tablet.jpg`,
            commonOptions
          )
        },
        subHeading:
          'Get personalized insights on your car’s value and track it over time with Carvana Value Tracker.'
      },
      {
        backgroundColor: background.medium,
        cta: {
          action: '/insurance',
          text: 'Learn More'
        },
        heading: 'Insurance coverage in 3 clicks™',
        image: {
          alt: 'Carvana insurance built with Root',
          desktop: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/carvana-insurance-desktop.jpg`,
            commonOptions
          ),
          mobile: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/carvana-insurance-mobile.jpg`,
            commonOptions
          ),
          tablet: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/carvana-insurance-tablet.jpg`,
            commonOptions
          )
        },
        subHeading:
          'Carvana Insurance powered by Root is designed for you. Get quick, custom coverage in 3 clicks™ while we handle the rest.'
      },
      {
        backgroundColor: reference.slate['700'],
        cta: {
          action: handleAppraisalModalCtaClick,
          href: '/sell-my-car',
          nativeIncluded: isTradeCtaEligible ? false : undefined,
          nativePayload: isTradeCtaEligible ? undefined : nativeStcPayload,
          text: 'Get Your Offer'
        },
        heading: 'Sell your car to Carvana',
        image: {
          alt: 'A Carvana employee at a customer’s house, delivering a check, with the customer’s car on a Carvana hauler in the background',
          desktop: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/sell-to-carvana-desktop.jpg`,
            commonOptions
          ),
          mobile: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/sell-to-carvana-mobile.jpg`,
            commonOptions
          ),
          tablet: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/sell-to-carvana-tablet.jpg`,
            commonOptions
          )
        },
        mobileImageHeight: 'h-[357px]',
        subHeading: 'Get a real offer in under 2 minutes.'
      }
    ],
    [handleAppraisalModalCtaClick, isTradeCtaEligible, onWatchVideoClick]
  );
}
